import { Box, Typography, Grid, CircularProgress, Divider, Card } from '@mui/material';
import { Formik, Form } from 'formik';
import axios from 'axios';
import React, { useState, useEffect } from "react";
import { READ_COMMERCIAL_INVOICE } from '../../../services/apiRoutes';
import CustomCardView from '../../common/CustomCardView';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from '../../common/DetailsPage/DetailsParticulars';
import { convertNumberToWords, printFunction } from '../../../services/apiServices';
import CustomPrintModel from '../../common/CustomPrint';
import { getRoundAmount } from '../../../services/CommonServices';
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from '../../../constants/translationHelper';


const CommercialInvoiceView = ({ handleClose, commercialInvoiceId }) => {
    const companyDateFormat = useCompanyDateFormat();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataParticular, setDataParticular] = useState(null);
    const [totalQty, setTotalQty] = useState(0); // State for total quantity
    const [totalRate, setTotalRate] = useState(0); // State for total rate
    const [amountData, setAmount] = useState(0);
    const [amountText, setAmountText] = useState(0);
    const [currencyName, setCurrencyName] = useState('');

    const number = amountText || '';
    const amountInWords = convertNumberToWords(number);
    useEffect(() => {
        if (!commercialInvoiceId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_COMMERCIAL_INVOICE(commercialInvoiceId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (response.data.success) {
                    const invoiceData = response.data.data;
                    const symbol = invoiceData?.currency?.currency_symbol || '';
                    const symbolName = invoiceData?.currency?.currency_name || '';
                    const particulars = invoiceData.particulars.map(particular => ({
                        qty: particular.qty || 0,
                        amount: parseFloat(particular.amount) || 0,
                        rate: `${symbol || ''} ${parseFloat(particular?.rate || 0).toFixed(2)}`,
                        amounts: `${symbol || ''} ${parseFloat(particular?.amount || 0).toFixed(2)}`,
                        productName: particular.products.productName,
                        unitName: particular.productsUnit.unitName,
                    }));
                    const totalQuantity = particulars
                        .reduce((total, particular) => total + (parseFloat(particular.qty) || 0), 0)
                    const amountTotal = particulars.reduce((acc, item) => acc + item.amount, 0);
                    const roundedAmount = getRoundAmount(amountTotal);

                    setAmount(`${symbol} ${roundedAmount.toFixed(2)}`);
                    setAmountText(roundedAmount);
                    setData(invoiceData);
                    setDataParticular(particulars);
                    setTotalQty(totalQuantity);
                    setCurrencyName(symbolName);
                }

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [commercialInvoiceId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const getLastSuffix = (url) => {
        if (url) {
            const parts = url.split('/');
            return parts[parts.length - 1];
        }
        return '';
    };

    const values = {
        supplierNames: data?.clientCompany?.clientCompanyName || '',
        reference: data?.reference || '',
        portOfLoading: `${data?.loading?.portName || ""}, ${data?.loading?.country?.name || ""}`.trim(),
        portOfDestination: `${data?.destination?.portName || ""}, ${data?.destination?.country?.name || ""}`.trim(),
        commercialInvoiceNo: data?.commercialInvoiceNo || '',
        date: companyDateFormat(data?.date) || '',
        dueDate: companyDateFormat(data?.dueDate) || '',
        note: data?.note || '',
        bookingNumber: data?.booking?.bookingNumber || '',
        ciUpload: getLastSuffix(data?.ciUpload || ''),
        ciUploadUrl: data?.ciUpload || '',
        currency: data?.currency?.currency || '',
        proformaInvoiceNo: data?.ProformaInvoice?.proformaInvoiceNo || '',
        particulars: data?.particulars.map(particular => ({
            particularName: parseInt(particular.particularName, 10),
            qty: particular.qty,
            rate: particular.rate,
            unit: parseInt(particular.unit, 10),
            description: particular.description,
        })) || [],
    };

    const labels = [
        { key: 'supplierNames', label: "Supplier", sm: 12, md: 4, },
        { key: 'dueDate', label: "Due Date", sm: 12, md: 4, },
        { key: 'commercialInvoiceNo', label: "Commercial Invoice", sm: 12, md: 4, },
        { key: 'bookingNumber', label: 'Booking Number', sm: 12, md: 4, },
        { key: 'currency', label: "currency", sm: 12, md: 4, },
        { key: 'date', label: "Date", sm: 12, md: 4, },
        { key: 'proformaInvoiceNo', label: "proforma Invoice", sm: 12, md: 4, },
        { key: 'reference', label: "Reference", sm: 12, md: 4, },
        { key: 'portOfLoading', label: 'port Of Loading', sm: 12, md: 4, },
        { key: 'portOfDestination', label: 'port Of Destination', sm: 12, md: 4, },
    ];


    const particularsColumns = [

        {
            id: 'productName',
            label: 'Product Name',
            accessor: 'productName',
        },
        {
            id: 'qty',
            label: 'Quantity',
            accessor: 'qty',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'rate',
            label: 'Rate',
            accessor: 'rate',
            styleAccessor: {
                textAlign: 'right',
            },
        },
        {
            id: 'unitName',
            label: 'Unit',
            accessor: 'unitName',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'amounts',
            label: 'Amount',
            accessor: 'amounts',
            styleAccessor: {
                textAlign: 'right',
            },
        },
    ];


    const footerData = [
        {
            label: 'Total',
            footerValue: '',
            style: {
                textAlign: 'right',
            },
            colSpan: 1,

        },
        {
            label: '',
            footerValue: totalQty,
            style: {
                textAlign: 'center',

            },

        },
        {
            label: '',
            footerValue: amountData,
            style: {
                textAlign: 'right',

            },
            colSpan: 3,

        },
    ];
    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId);
    };



    return (
        <>
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik
                    initialValues={values}
                    enableReinitialize
                >
                    {() => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                        {t("Commercial Invoice")}

                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose}>
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={0} sx={{ px: "0%" }}>
                                <Grid item xs={12} md={12} >
                                    <CustomCardView
                                        title="Bill To"
                                        label={labels}
                                        data={values}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Particulars")}
                                        </Typography>
                                    </Box>
                                    <DetailsParticulars
                                        title={'Particulars'}
                                        columns={particularsColumns}
                                        data={dataParticular}
                                        footerData={footerData}
                                    />
                                </Grid>

                                <Grid item xs={7}>
                                    <br />
                                    <Box>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("In Words")}
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "uppercase" }}>
                                            {currencyName} {amountInWords}
                                        </Typography>
                                    </Box>
                                    <br />
                                    <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("CI Document")} :
                                        </Typography>
                                        <a href={values.ciUploadUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff", textDecoration: "none" }}>
                                            {values.ciUpload}
                                        </a>
                                    </Box>

                                    <Box sx={{ pt: 5 }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("Note")} :
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "capitalize" }}>
                                            {values.note}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={5}>
                                    <br />
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Summary")}
                                        </Typography>
                                    </Box>
                                    <Card sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 400 }} variant="outlined">
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Total Quantity")}:</Typography>
                                            <Typography>{totalQty}</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Amount")}:</Typography>
                                            <Typography> {amountData}</Typography>
                                        </Box>

                                        <Divider sx={{ my: 0 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Grand Total")}:</Typography>
                                            <Typography fontWeight="bold">{amountData}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>
            <CustomPrintModel contentId="contentId">
                <Typography style={{ textAlign: "center" }}>Commercial Invoice</Typography>
            </CustomPrintModel >
        </>
    );
};

export default CommercialInvoiceView;
